import React, { useCallback, useEffect } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';

import BasicChip from 'components/display/chips/basicChip';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';
import { MODAL_Z_INDEX } from '../../constants/layers';

interface StyledProps {
    zIndex?: number;
    isMobile?: boolean;
    width?: string;
    height?: string;
    maxHeight?: string;
}
interface HeaderContainerStyledProps {
    headerStyle?: React.CSSProperties;
}
interface ChildContainerStyledProps {
    childContainerStyle?: React.CSSProperties;
}
interface DescriptionStyledProps {
    descriptionStyle?: React.CSSProperties;
}
interface FooterContainerStyledProps {
    footerStyle?: React.CSSProperties;
}

interface BasicModalProps {
    header?: string;
    backIcon?: boolean;
    onBackIconClick?: () => void;
    description?: string;
    width?: string;
    height?: string;
    containerBodyStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    descriptionStyle?: React.CSSProperties;
    childContainerStyle?: React.CSSProperties;
    containerId?: string;
    handleClose?: () => void;
    zIndex?: number;
    maxHeight?: string;
    className?: string;
    tag?: string;
    footer?: React.ReactNode;
    footerStyle?: React.CSSProperties;
}

const CustomCloseButton = styled(ICONS.close)`
    cursor: pointer;
    margin-left: auto;
`;

const BasicModal: React.FC<React.PropsWithChildren<BasicModalProps>> = ({
    header,
    description,
    descriptionStyle,
    children,
    width = '400px',
    height,
    headerStyle,
    childContainerStyle,
    containerId,
    handleClose,
    zIndex = MODAL_Z_INDEX,
    containerBodyStyle,
    maxHeight = '90vh',
    className,
    tag,
    footer,
    footerStyle,
    backIcon,
    onBackIconClick,
}) => {
    const isMobile = useMediaQuery('(max-width:991px)');

    const escFunction = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && handleClose) {
                handleClose();
            }
        },
        [handleClose],
    );

    useEffect(() => {
        document.addEventListener('keydown', escFunction, false);

        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);

    return (
        <Container zIndex={zIndex} className={className}>
            <ContainerBody
                id={containerId}
                isMobile={isMobile}
                width={width}
                height={height}
                style={containerBodyStyle}
                onClick={(e: any) => {
                    e.stopPropagation();
                }}
                maxHeight={maxHeight}
            >
                {header && (
                    <Header headerStyle={headerStyle}>
                        <HeaderContent>
                            <HeaderWithBackIcon>
                                {backIcon && (
                                    <BackIcon onClick={onBackIconClick} />
                                )}
                                <HeaderText>{header}</HeaderText>
                            </HeaderWithBackIcon>
                            {tag && <BasicChip title={tag} chipTheme="beta" />}
                        </HeaderContent>
                        {handleClose && (
                            <CustomCloseButton onClick={handleClose} />
                        )}
                    </Header>
                )}
                {description && (
                    <Description descriptionStyle={descriptionStyle}>
                        {description}
                    </Description>
                )}
                <ChildContainer childContainerStyle={childContainerStyle}>
                    {children}
                </ChildContainer>
                {footer && <Footer footerStyle={footerStyle}>{footer}</Footer>}
            </ContainerBody>
        </Container>
    );
};

const Container = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'zIndex',
})<StyledProps>(({ zIndex }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: zIndex,
    background: 'linear-gradient(180deg, rgba(38,50,56,.5), rgba(38,50,56,.5))',
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    width: '100%',
    height: '100%',
}));

const ContainerBody = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['isMobile', 'width', 'maxHeight', 'height'].includes(prop),
})<StyledProps>(({ isMobile, width, maxHeight, height }) => ({
    backgroundColor: aampeTheme.colors.white,
    width: isMobile ? '90%' : width,
    height: height ? height : 'fit-content',
    borderRadius: aampeTheme.borderRadius.default,
    maxHeight: maxHeight,
    overflowY: 'auto',
}));

const Header = styled.div.withConfig({
    shouldForwardProp: (prop) => !['headerStyle'].includes(prop),
})<HeaderContainerStyledProps>(({ headerStyle }) => ({
    borderRadius: `${aampeTheme.borderRadius.default} ${aampeTheme.borderRadius.default} 0px 0px`,
    backgroundColor: aampeTheme.colors.solidA01,
    padding: aampeTheme.spacing4(6),
    color: aampeTheme.colors.white,
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...(headerStyle ?? {}),
}));

const HeaderContent = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
});

const HeaderText = styled.span({
    // Inherit all text styles from parent Header
});
const Description = styled.div.withConfig({
    shouldForwardProp: (prop) => !['descriptionStyle'].includes(prop),
})<DescriptionStyledProps>(({ descriptionStyle }) => ({
    padding: aampeTheme.spacing4(5, 4, 0, 8),
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: 400,
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    ...(descriptionStyle ?? {}),
}));

const ChildContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['childContainerStyle'].includes(prop),
})<ChildContainerStyledProps>(({ childContainerStyle }) => ({
    borderRadius: '0px 0px 10px 10px',
    padding: aampeTheme.spacing4(6),
    ...(childContainerStyle ?? {}),
}));
const Footer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['footerStyle'].includes(prop),
})<FooterContainerStyledProps>(({ footerStyle }) => ({
    padding: aampeTheme.spacing4(6),
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: aampeTheme.colors.white,
    borderRadius: '0 0 10px 10px',
    ...(footerStyle ?? {}),
}));

const BackIcon = styled(ICONS.compactLeftArrow)({
    cursor: 'pointer',
    color: aampeTheme.colors.textAccessibleWG01,
});

const HeaderWithBackIcon = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(1),
    alignItems: 'center',
    marginLeft: aampeTheme.spacing4(-1),
});
export default BasicModal;
